import React, { useState, useEffect } from 'react';
import { X, Save, RefreshCw, Percent, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getFeeSettings, updateFeeSettings } from '../lib/admin';

interface Props {
  onClose: () => void;
  onUpdate: () => void;
}

export default function FeeSettingsModal({ onClose, onUpdate }: Props) {
  const [platformFee, setPlatformFee] = useState<string>('20');
  const [gatewayFee, setGatewayFee] = useState<string>('20');
  const [originalPlatformFee, setOriginalPlatformFee] = useState<string>('20');
  const [originalGatewayFee, setOriginalGatewayFee] = useState<string>('20');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadFeeSettings();
  }, []);

  const loadFeeSettings = async () => {
    try {
      setLoading(true);
      setError('');

      const { platformFeeRate, gatewayFeeRate } = await getFeeSettings();
      
      // Convert to percentage for display
      const platformFeePercentage = platformFeeRate.toString();
      const gatewayFeePercentage = gatewayFeeRate.toString();
      
      setPlatformFee(platformFeePercentage);
      setGatewayFee(gatewayFeePercentage);
      setOriginalPlatformFee(platformFeePercentage);
      setOriginalGatewayFee(gatewayFeePercentage);
    } catch (err) {
      console.error('Error loading fee settings:', err);
      setError('Failed to load fee settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess(false);

      // Validate inputs
      const platformFeeValue = parseFloat(platformFee);
      const gatewayFeeValue = parseFloat(gatewayFee);

      if (isNaN(platformFeeValue) || platformFeeValue < 0 || platformFeeValue > 100) {
        setError('Platform fee must be a valid percentage between 0 and 100');
        return;
      }

      if (isNaN(gatewayFeeValue) || gatewayFeeValue < 0 || gatewayFeeValue > 100) {
        setError('Gateway fee must be a valid percentage between 0 and 100');
        return;
      }

      // Update fee settings
      await updateFeeSettings(platformFeeValue, gatewayFeeValue);

      // Log admin action
      await supabase.rpc('log_admin_action', {
        action_type_param: 'update_fee_settings',
        target_type_param: 'system_settings',
        target_id_param: '00000000-0000-0000-0000-000000000000',
        old_values_param: { 
          platform_fee: originalPlatformFee, 
          gateway_fee: originalGatewayFee 
        },
        new_values_param: { 
          platform_fee: platformFee, 
          gateway_fee: gatewayFee 
        }
      });

      setSuccess(true);
      
      // Update original values
      setOriginalPlatformFee(platformFee);
      setOriginalGatewayFee(gatewayFee);
      
      // Notify parent component
      onUpdate();
      
      // Close after a delay
      setTimeout(() => {
        if (success) onClose();
      }, 1500);
    } catch (err) {
      console.error('Error saving fee settings:', err);
      setError('Failed to save fee settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = platformFee !== originalPlatformFee || gatewayFee !== originalGatewayFee;

  return (
    <div className="fixed inset-0 bg-black/20 flex items-center justify-center p-4 z-50">
      <div className="card-pastel p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Percent className="w-5 h-5 text-gray-800" />
            <h3 className="text-lg font-semibold text-gray-800 font-poppins">Fee Settings</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/60 rounded-full transition-colors text-gray-500 hover:text-gray-800"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-md flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="font-poppins">{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-50 text-green-700 p-3 rounded-md font-poppins">
            Fee settings updated successfully!
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pastel-purple-500"></div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
                  Platform Fee (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={platformFee}
                    onChange={(e) => setPlatformFee(e.target.value)}
                    className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
                    placeholder="20"
                    step="0.01"
                    min="0"
                    max="100"
                    disabled={saving}
                  />
                  <span className="absolute right-3 top-2.5 text-gray-500">%</span>
                </div>
                <p className="text-xs text-gray-500 mt-1 font-poppins">
                  This is the fee that Giftyfy charges for using the platform
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
                  Payment Gateway Fee (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={gatewayFee}
                    onChange={(e) => setGatewayFee(e.target.value)}
                    className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
                    placeholder="20"
                    step="0.01"
                    min="0"
                    max="100"
                    disabled={saving}
                  />
                  <span className="absolute right-3 top-2.5 text-gray-500">%</span>
                </div>
                <p className="text-xs text-gray-500 mt-1 font-poppins">
                  This is the fee charged by the payment processor (PayHere)
                </p>
              </div>

              <div className="bg-pastel-blue-50 p-3 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-pastel-blue-700 font-medium font-poppins">Total Fee: {(parseFloat(platformFee || '0') + parseFloat(gatewayFee || '0')).toFixed(2)}%</span>
                </div>
                <p className="text-pastel-blue-700 text-sm font-poppins">
                  This is the total fee that will be added to each transaction. The original gift amount will be transferred to the registry owner.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={loadFeeSettings}
                disabled={loading || saving}
                className="flex items-center space-x-2 px-4 py-2 bg-white/60 text-gray-800 rounded-md hover:bg-white/80 disabled:opacity-50 transition-colors font-poppins"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Reset</span>
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 disabled:opacity-50 transition-colors font-poppins"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}